import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Edit, ArrowBack, Refresh, WarningOutlined } from "@mui/icons-material";
import {
  getExistingReferences,
  selectExistingSDT,
  DocumentReference,
} from "../../../utils/taskpane";
import { ReferenceValidationResult } from "../types/reference-validation";
import { useValidateReferencesMutation } from "../api/playbookApi";
import { useAssemblyContext } from "../context/AssemblyContext";

interface ExistingReferencesProps {
  onEditReference: (reference: DocumentReference) => void;
  onBack: () => void;
}

const ExistingReferences: React.FC<ExistingReferencesProps> = ({ onEditReference, onBack }) => {
  const ctx = useAssemblyContext();
  const [references, setReferences] = useState<DocumentReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [validationResults, setValidationResults] = useState<
    Map<string, ReferenceValidationResult>
  >(new Map());

  const [validateReferences] = useValidateReferencesMutation();

  const validateExistingReferences = async (referenceIds: string[]) => {
    try {
      const results = await validateReferences({ ctx, referenceIds }).unwrap();
      const resultMap = new Map<string, ReferenceValidationResult>();
      results.forEach((result) => {
        resultMap.set(result.stableObjectIdentifier, result);
      });
      setValidationResults(resultMap);
    } catch (err) {
      console.error("Failed to validate references:", err);
    }
  };

  const loadReferences = async () => {
    try {
      setError(null);
      const existingRefs = await getExistingReferences();
      setReferences(existingRefs);
      if (existingRefs.length > 0) {
        await validateExistingReferences(existingRefs.map((ref) => ref.stableObjectIdentifier));
      }
    } catch (err) {
      setError("Failed to load existing references");
      console.error("Error loading references:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadReferences();
  };

  const handleEditAndShow = async (reference: DocumentReference) => {
    try {
      const success = await selectExistingSDT(reference.stableObjectIdentifier);
      if (!success) {
        setError("Failed to locate reference in document");
      } else {
        onEditReference(reference);
      }
    } catch (err) {
      setError("Failed to show reference in document");
      console.error("Error showing reference:", err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading existing references...</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} p={2}>
      <Stack direction="row" alignItems="center">
        <Button
          startIcon={<ArrowBack fontSize="small" />}
          variant="text"
          sx={{ color: "text.secondary" }}
          onClick={onBack}
          size="small"
        >
          Back
        </Button>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1">
          Existing References {references.length > 0 && `(${references.length})`}
        </Typography>
        <Button
          variant="text"
          size="small"
          startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {references.length === 0 ? (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            p: 3,
            textAlign: "center",
            backgroundColor: "grey.50",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            No references found in the document.
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
            References will appear here once you add them to the document.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {references.map((reference) => {
            const validation = validationResults.get(reference.stableObjectIdentifier);
            return (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                borderRadius={1}
                p={1}
                key={reference.stableObjectIdentifier}
                sx={{
                  "&:hover": { backgroundColor: "action.hover" },
                  cursor: "pointer",
                  border: 1,
                  borderColor: "divider",
                }}
                onClick={() => handleEditAndShow(reference)}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>{reference.label}</Typography>
                  {validation && !validation.isValid && (
                    <WarningOutlined
                      fontSize="small"
                      color="warning"
                      sx={{ cursor: "help" }}
                      titleAccess={validation.message}
                    />
                  )}
                </Stack>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditAndShow(reference);
                  }}
                  title="Edit reference"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default ExistingReferences;
